import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import LaunchScreen from './components/LaunchScreen';
import { Toaster } from './components/ui/sonner';

import Home from './pages/home';
import MarketingInsights from './pages/MarketingInsights';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Learn from './pages/Learn';
import CoursePlayer from './pages/CoursePlayer';
import Credentials from './pages/Credentials';
import Connect from './pages/Connect';
import Rewards from './pages/Rewards';
import Jobs from './pages/Jobs';
import Reentry from './pages/Reentry';
import Community from './pages/Community';
import Library from './pages/Library';
import Health from './pages/Health';
import Legal from './pages/Legal';
import Admin from './pages/Admin';
import Verify from './pages/Verify';
import EmployerDashboard from './pages/EmployerDashboard';
import CaseManager from './pages/CaseManager';
import AiTutor from './pages/AiTutor';
import ProgressReport from './pages/ProgressReport';
import Instructor from './pages/Instructor';
import Messages from './pages/Messages';
import Leaderboard from './pages/Leaderboard';
import Prototype from './pages/Prototype';

import Marketplace from './pages/Marketplace';

// Marketplace
import MarketplaceCatalog from './pages/marketplace/MarketplaceCatalog';
import ProviderPortal from './pages/marketplace/ProviderPortal';
import ProviderCourses from './pages/marketplace/ProviderCourses';
import CourseBuilder from './pages/marketplace/CourseBuilder';
import ProviderAnalytics from './pages/marketplace/ProviderAnalytics';
import ProviderSettings from './pages/marketplace/ProviderSettings';
import MarketplaceAdmin from './pages/marketplace/MarketplaceAdmin';
import MarketplaceApply from './pages/marketplace/MarketplaceApply';
import ProviderPublicProfile from './pages/marketplace/ProviderPublicProfile';

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/verify" element={<Verify />} />
      <Route path="/marketplace/apply" element={<MarketplaceApply />} />
      <Route path="/marketplace/providers/:id" element={<ProviderPublicProfile />} />
      <Route path="/prototype" element={<Prototype />} />

      {/* Learner / all-auth */}
      <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
      <Route path="/learn" element={<ProtectedRoute><Layout><Learn /></Layout></ProtectedRoute>} />
      <Route path="/learn/:pathwayId/:courseId?/:lessonId?" element={<ProtectedRoute><Layout><CoursePlayer /></Layout></ProtectedRoute>} />
      <Route path="/credentials" element={<ProtectedRoute><Layout><Credentials /></Layout></ProtectedRoute>} />
      <Route path="/connect" element={<ProtectedRoute><Layout><Connect /></Layout></ProtectedRoute>} />
      <Route path="/rewards" element={<ProtectedRoute><Layout><Rewards /></Layout></ProtectedRoute>} />
      <Route path="/jobs" element={<ProtectedRoute><Layout><Jobs /></Layout></ProtectedRoute>} />
      <Route path="/jobs/employer" element={<ProtectedRoute requiredRole="employer_partner"><Layout><EmployerDashboard /></Layout></ProtectedRoute>} />
      <Route path="/marketplace" element={<ProtectedRoute requiredRole="employer_partner"><Layout><Marketplace /></Layout></ProtectedRoute>} />
      <Route path="/reentry" element={<ProtectedRoute><Layout><Reentry /></Layout></ProtectedRoute>} />
      <Route path="/community" element={<ProtectedRoute><Layout><Community /></Layout></ProtectedRoute>} />
      <Route path="/library" element={<ProtectedRoute><Layout><Library /></Layout></ProtectedRoute>} />
      <Route path="/health" element={<ProtectedRoute><Layout><Health /></Layout></ProtectedRoute>} />
      <Route path="/legal" element={<ProtectedRoute><Layout><Legal /></Layout></ProtectedRoute>} />

      {/* Admin / Staff */}
      <Route path="/admin" element={<ProtectedRoute><Layout><Admin /></Layout></ProtectedRoute>} />
      <Route path="/case-manager" element={<ProtectedRoute><Layout><CaseManager /></Layout></ProtectedRoute>} />
      <Route path="/instructor" element={<ProtectedRoute><Layout><Instructor /></Layout></ProtectedRoute>} />

      {/* Marketplace – Admin */}
      <Route path="/marketplace/catalog" element={<ProtectedRoute><Layout><MarketplaceCatalog /></Layout></ProtectedRoute>} />
      <Route path="/marketplace/admin" element={<ProtectedRoute requiredRole="facility_admin"><Layout><MarketplaceAdmin /></Layout></ProtectedRoute>} />
      <Route path="/marketplace/admin/applications" element={<ProtectedRoute requiredRole="facility_admin"><Layout><MarketplaceAdmin tab="applications" /></Layout></ProtectedRoute>} />
      <Route path="/marketplace/admin/content" element={<ProtectedRoute requiredRole="facility_admin"><Layout><MarketplaceAdmin tab="content" /></Layout></ProtectedRoute>} />
      <Route path="/marketplace/admin/providers" element={<ProtectedRoute requiredRole="facility_admin"><Layout><MarketplaceAdmin tab="providers" /></Layout></ProtectedRoute>} />

      {/* Marketplace – Provider */}
      <Route path="/marketplace/provider" element={<ProtectedRoute requiredRole="provider_partner"><Layout><ProviderPortal /></Layout></ProtectedRoute>} />
      <Route path="/marketplace/provider/courses" element={<ProtectedRoute requiredRole="provider_partner"><Layout><ProviderCourses /></Layout></ProtectedRoute>} />
      <Route path="/marketplace/provider/courses/new" element={<ProtectedRoute requiredRole="provider_partner"><Layout><CourseBuilder /></Layout></ProtectedRoute>} />
      <Route path="/marketplace/provider/analytics" element={<ProtectedRoute requiredRole="provider_partner"><Layout><ProviderAnalytics /></Layout></ProtectedRoute>} />
      <Route path="/marketplace/provider/settings" element={<ProtectedRoute requiredRole="provider_partner"><Layout><ProviderSettings /></Layout></ProtectedRoute>} />

      <Route path="/ai-tutor" element={<ProtectedRoute><Layout><AiTutor /></Layout></ProtectedRoute>} />
      <Route path="/progress-report" element={<ProtectedRoute><Layout><ProgressReport /></Layout></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Layout><Messages /></Layout></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><Layout><Leaderboard /></Layout></ProtectedRoute>} />
      <Route path="/marketing-insights" element={<ProtectedRoute><Layout><MarketingInsights /></Layout></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

import DemoGate from './components/DemoGate';

function App() {
  const [gateUnlocked, setGateUnlocked] = useState(false);
  const [launched, setLaunched] = useState(() => sessionStorage.getItem('aos_launch_v1') === '1');
  const skipLaunch = typeof window !== 'undefined' && (window.location.pathname.startsWith('/prototype') || window.location.pathname === '/');

  if (!gateUnlocked) {
    return <DemoGate onUnlock={() => setGateUnlocked(true)} />;
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          {!launched && !skipLaunch && <LaunchScreen onDone={() => setLaunched(true)} />}
          <AppRoutes />
          <Toaster
            position="top-right"
            theme="dark"
            richColors={false}
            toastOptions={{
              style: {
                background: '#1E1E1E',
                border: '1px solid #2A2A2A',
                color: '#EEEEEE',
                fontFamily: 'Inter, sans-serif',
                fontSize: '13px',
                borderRadius: '10px',
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
